import { colors } from '@client/new/components/colors';
import { useRef, useState } from 'react';
import { icon, wrapper } from './dropZone.css';
import { Typography } from '../typography/typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFiles } from '@fortawesome/pro-regular-svg-icons';
import { clsx } from 'clsx';

export type DropZoneProps = {
  onChange: (file: File[]) => void;
  infoText?: string;
  accept: string[];
  onError?: (message: string) => void;
  multiple?: boolean;
  className?: string;
  hideIcon?: boolean;
  bgColor?: string;
  textClassName?: string;
};

export const DropZone = (p: DropZoneProps) => {
  const [dragOver, setDragOver] = useState(false);
  const inputFile = useRef<HTMLInputElement | null>(null);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    p.onChange(Object.values(e.target.files));
    // must happen after we get the file from the input
    if (inputFile.current) inputFile.current.value = '';
  };

  const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragEnd = () => setDragOver(false);

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length === 0) return;
    let files = Object.values(e.dataTransfer.files);
    if (p?.accept) {
      files = files.filter((f) =>
        p.accept.some((pattern) =>
          pattern.endsWith('/*')
            ? f.type.startsWith(pattern.replace('/*', '/'))
            : pattern === f.type,
        ),
      );
    }
    p.onChange(files);
  };

  return (
    <div
      onDragOverCapture={onDragStart}
      onDragLeaveCapture={onDragEnd}
      onClick={() => inputFile.current?.click()}
      className={clsx(wrapper, p?.className)}
      style={{
        background: dragOver ? colors.secondary200 : p?.bgColor || 'white',
        borderColor: dragOver ? colors.secondary900 : colors.inactive,
      }}
      onDropCapture={onDrop}
    >
      <input
        style={{ display: 'none' }}
        ref={inputFile}
        type="file"
        onChange={onInputChange}
        accept={p.accept?.join(', ')}
        multiple={!!p?.multiple}
      />

      {p.hideIcon ? null : <FontAwesomeIcon icon={faFiles} className={icon} />}
      <Typography variant="title" className={clsx(p?.textClassName)}>
        Click to browse or <br /> drag and drop your files
      </Typography>

      {p.infoText && <Typography variant="title">{p.infoText}</Typography>}
    </div>
  );
};
